/**
 * Strip common quote suffixes (including doubled USDT) for TradingView search.
 * A bare `USD` suffix is only stripped after a separator (`BTC-USD`), because
 * tokens such as TUSD, PYUSD and RLUSD end in USD.
 */
export function normalizeBaseSymbolForTradingView(raw: string): string {
  const trimmed = raw.trim().toUpperCase();
  let s = trimmed.replace(/[-_/](USD)$/, "").replace(/[-_/]/g, "");
  const suffixes = ["USDT", "USDC", "BUSD", "PERP", "PERPETUAL"];
  let changed = true;
  while (changed && s.length > 0) {
    changed = false;
    for (const suf of suffixes) {
      if (s.endsWith(suf) && s.length > suf.length) {
        s = s.slice(0, -suf.length);
        changed = true;
      }
    }
  }
  return s.replace(/[^A-Z0-9]/g, "") || trimmed;
}

export interface TradingViewSymbol {
  symbol: string;
  description: string;
  type: string;
  exchange: string;
  typespecs?: string[];
  prefix?: string;
  source_id?: string;
}

interface TradingViewSymbolResponse {
  symbols: TradingViewSymbol[];
}

export interface TradingViewMatch {
  exchange: string;
  symbol: string;
}

/** One `EXCHANGE:PAIR` market that trades the searched ticker against USDT. */
export interface TradingViewCandidate {
  /** TradingView exchange prefix, e.g. `WEEX`. */
  exchange: string;
  /** Exchange display name from search, used for preferred-exchange ranking. */
  exchangeName: string;
  /** Plain pair, e.g. `PODUSDT` or `PODUSDT.P`. */
  symbol: string;
  /** Contract multiplier, e.g. 1000 for `1000PEPEUSDT`. */
  multiplier: number;
}

export interface TradingViewQuote {
  close: number | null;
  volume: number | null;
}

export interface TradingViewSymbolOptions {
  /**
   * A price we already trust for this token (live exchange price or the
   * alert's trigger price). Used to pick the market that trades the same
   * token when several tokens share a ticker.
   */
  referencePrice?: number | null;
  /**
   * Return no match when every candidate is further than this fraction from
   * `referencePrice` (0.3 = 30%). Only pass it for a current price; an old
   * trigger price can legitimately be far from today's price.
   */
  rejectAbove?: number;
}

const stripHtml = (value: string) => value.split(/<[^>]*>?/g).join("");

const plainSymbol = (value: string) => stripHtml(value).toUpperCase();

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const PREFERRED_EXCHANGES = [
  "Binance",
  "Bybit",
  "Gate",
  "KuCoin",
  "Hyperliquid",
  "Aster",
];

// Candidates within this band of the closest price count as the same token,
// so ranking among them stays stable while the live price ticks.
const SAME_TOKEN_BAND = Math.log(1.15);

const getExchangePriority = (exchange: string): number => {
  const index = PREFERRED_EXCHANGES.findIndex(
    (preferred) => exchange.toLowerCase() === preferred.toLowerCase()
  );
  return index === -1 ? PREFERRED_EXCHANGES.length : index;
};

export const tradingViewTicker = (candidate: TradingViewCandidate) =>
  `${candidate.exchange}:${candidate.symbol}`;

/**
 * Keep only markets whose pair is exactly `<KEY>USDT` or `<KEY>USDT.P`.
 * Derived series (`_PREMIUM`, `.P_MPRICE`, `.P_IPRICE`) and other tickers that
 * merely contain the key are dropped. Multiplier pairs such as `1000PEPEUSDT`
 * are used only when no exact pair exists, and DEX pools (`<KEY>USDT_60F9C0`)
 * only when neither exists.
 */
export function collectTradingViewCandidates(
  symbols: TradingViewSymbol[],
  searchKey: string
): TradingViewCandidate[] {
  const pattern = new RegExp(
    `^(1(?:0{1,9}))?${escapeRegExp(searchKey)}USDT(?:\\.P|(_[0-9A-F]+))?$`
  );
  const exact: TradingViewCandidate[] = [];
  const multiplied: TradingViewCandidate[] = [];
  const dexPools: TradingViewCandidate[] = [];
  const seen = new Set<string>();

  for (const item of symbols) {
    if (!item.typespecs?.includes("crypto")) continue;
    const symbol = plainSymbol(item.symbol);
    const match = pattern.exec(symbol);
    if (!match) continue;

    const candidate: TradingViewCandidate = {
      exchange: (item.prefix ?? item.source_id ?? item.exchange).toUpperCase(),
      exchangeName: item.exchange,
      symbol,
      multiplier: match[1] ? Number(match[1]) : 1,
    };
    const ticker = tradingViewTicker(candidate);
    if (seen.has(ticker)) continue;
    seen.add(ticker);
    if (match[2]) {
      if (candidate.multiplier === 1) dexPools.push(candidate);
    } else {
      (candidate.multiplier === 1 ? exact : multiplied).push(candidate);
    }
  }

  if (exact.length > 0) return exact;
  return multiplied.length > 0 ? multiplied : dexPools;
}

/**
 * Pick one candidate deterministically. The result never depends on the order
 * TradingView returned the symbols in.
 *
 * 1. With quotes, markets with no price or no volume are dropped (dead pairs).
 * 2. With a reference price, only markets close to it are kept (same token).
 * 3. The rest are ranked by preferred exchange, then USD volume, then ticker.
 */
export function rankTradingViewCandidates(
  candidates: TradingViewCandidate[],
  quotes: Map<string, TradingViewQuote> | null,
  options: TradingViewSymbolOptions = {}
): TradingViewMatch | null {
  if (candidates.length === 0) {
    return null;
  }

  const quoteFor = (candidate: TradingViewCandidate) =>
    quotes?.get(tradingViewTicker(candidate));
  const unitPrice = (candidate: TradingViewCandidate) => {
    const close = quoteFor(candidate)?.close;
    return close && close > 0 ? close / candidate.multiplier : null;
  };
  const usdVolume = (candidate: TradingViewCandidate) => {
    const quote = quoteFor(candidate);
    return quote?.close && quote?.volume ? quote.close * quote.volume : 0;
  };

  let pool = candidates;

  if (quotes) {
    const alive = pool.filter(
      (candidate) => unitPrice(candidate) !== null && usdVolume(candidate) > 0
    );
    if (alive.length > 0) {
      pool = alive;
    }
  }

  const reference = options.referencePrice;
  if (quotes && reference && reference > 0) {
    const priced = pool
      .map((candidate) => {
        const price = unitPrice(candidate);
        return {
          candidate,
          deviation: price === null ? Infinity : Math.abs(Math.log(price / reference)),
        };
      })
      .filter((entry) => Number.isFinite(entry.deviation));

    if (priced.length > 0) {
      const best = Math.min(...priced.map((entry) => entry.deviation));
      if (
        options.rejectAbove !== undefined &&
        best > Math.log(1 + options.rejectAbove)
      ) {
        return null;
      }
      pool = priced
        .filter((entry) => entry.deviation <= best + SAME_TOKEN_BAND)
        .map((entry) => entry.candidate);
    }
  }

  const sorted = [...pool].sort((a, b) => {
    const priority =
      getExchangePriority(a.exchangeName) - getExchangePriority(b.exchangeName);
    if (priority !== 0) return priority;
    const volume = usdVolume(b) - usdVolume(a);
    if (volume !== 0) return volume;
    const tickerA = tradingViewTicker(a);
    const tickerB = tradingViewTicker(b);
    return tickerA < tickerB ? -1 : tickerA > tickerB ? 1 : 0;
  });

  const match = sorted[0];
  return { exchange: match.exchange, symbol: match.symbol };
}

const getFetch = () =>
  typeof globalThis === "undefined" ? undefined : globalThis.fetch;

async function queryTradingViewSymbols(text: string): Promise<TradingViewSymbol[]> {
  const fetchFn = getFetch();
  if (!fetchFn) {
    return [];
  }

  const endpoint = `https://symbol-search.tradingview.com/local_search/v3/?text=${encodeURIComponent(
    text
  )}&hl=1`;
  const response = await fetchFn(endpoint);

  if (!response.ok) {
    throw new Error("Failed to fetch symbol metadata");
  }

  const data = (await response.json()) as TradingViewSymbolResponse;
  return data.symbols ?? [];
}

/**
 * Last price and 24h volume per ticker from TradingView's scanner. The body is
 * sent as text/plain because the scanner's CORS policy does not allow a
 * Content-Type header. Returns null on any failure so ranking can fall back.
 */
async function queryTradingViewQuotes(
  tickers: string[]
): Promise<Map<string, TradingViewQuote> | null> {
  const fetchFn = getFetch();
  if (!fetchFn || tickers.length === 0) {
    return null;
  }

  const controller =
    typeof AbortController === "undefined" ? undefined : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), 4000) : undefined;

  try {
    const response = await fetchFn("https://scanner.tradingview.com/crypto/scan", {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        symbols: { tickers },
        columns: ["close", "volume"],
      }),
      signal: controller?.signal,
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as {
      data?: { s: string; d: [number | null, number | null] }[];
    };
    const quotes = new Map<string, TradingViewQuote>();
    for (const row of data.data ?? []) {
      quotes.set(row.s.toUpperCase(), { close: row.d?.[0] ?? null, volume: row.d?.[1] ?? null });
    }
    return quotes;
  } catch {
    return null;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export async function fetchTradingViewSymbol(
  rawSymbol: string,
  options: TradingViewSymbolOptions = {}
): Promise<TradingViewMatch | null> {
  if (!getFetch()) {
    return null;
  }

  const searchKey = normalizeBaseSymbolForTradingView(rawSymbol);
  if (!searchKey) {
    return null;
  }

  let candidates = collectTradingViewCandidates(
    await queryTradingViewSymbols(`${searchKey}USDT`),
    searchKey
  );
  if (candidates.length === 0) {
    candidates = collectTradingViewCandidates(
      await queryTradingViewSymbols(searchKey),
      searchKey
    );
  }
  if (candidates.length === 0) {
    return null;
  }

  const quotes = await queryTradingViewQuotes(candidates.map(tradingViewTicker));
  return rankTradingViewCandidates(candidates, quotes, options);
}
